import React, { useEffect, useRef, useState } from 'react';
import { DatabaseZap, Download, Upload, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const http = (path, opts = {}) => fetch(`${API}/api${path}`, { credentials: 'include', ...opts })
  .then(async (r) => { if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.detail || 'Request failed'); } return r; });

// Copy CMS content (settings, pages, gallery, shop…) from one environment to another via a JSON file.
export default function MigrationTab() {
  const [groups, setGroups] = useState([]);
  const [picked, setPicked] = useState({});
  const [busy, setBusy] = useState('');
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    http('/admin/migration/groups').then((r) => r.json()).then((g) => { setGroups(g); setPicked(Object.fromEntries(g.map((x) => [x.id, true]))); }).catch(() => {});
  }, []);

  const chosen = groups.filter((g) => picked[g.id]).map((g) => g.id);

  const doExport = async () => {
    if (!chosen.length) { toast.error('Pick at least one group to export.'); return; }
    setBusy('export');
    try {
      const r = await http(`/admin/migration/export?groups=${chosen.join(',')}`);
      const name = (/filename="?([^";]+)"?/.exec(r.headers.get('content-disposition') || '') || [])[1] || 'site-data.json';
      const a = document.createElement('a'); a.href = URL.createObjectURL(await r.blob()); a.download = name; a.click();
      toast.success('Export downloaded. Open the production site → Admin → Data Migration → Import to load it.');
    } catch (e) { toast.error(e.message); } finally { setBusy(''); }
  };

  const doImport = async (e) => {
    const f = e.target.files && e.target.files[0]; if (!f) return;
    if (!window.confirm(`Import "${f.name}"? Matching records on THIS site will be overwritten. Members, payments and meetings are never touched.`)) { e.target.value = ''; return; }
    setBusy('import'); setResult(null);
    try {
      const r = await http('/admin/migration/import', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: await f.text() });
      const d = await r.json(); setResult(d.imported);
      toast.success('Import complete. Reload the site to see the changes.');
    } catch (err) { toast.error(err.message); } finally { setBusy(''); e.target.value = ''; }
  };

  return (
    <div className="max-w-3xl" data-testid="migration-panel">
      <div className="flex items-center gap-2 mb-1"><DatabaseZap size={18} style={{ color: 'var(--maroon)' }} /><h2 className="font-title text-xl" style={{ color: 'var(--maroon)' }}>Data migration</h2></div>
      <p className="text-sm mb-4" style={{ color: 'var(--olive)' }}>
        Copy site content between the preview and production sites. <b>Export</b> here, then open the other site's Admin → Data Migration and <b>Import</b> the file.
        Records with the same id are overwritten; members, payments, meetings and email/bank credentials are never included.
      </p>

      <div className="bg-white rounded-lg p-5 border shadow-sm mb-4" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
        <h3 className="font-title text-lg mb-2" style={{ color: 'var(--navy)' }}>1. Export from this site</h3>
        <div className="space-y-1.5 mb-3">
          {groups.map((g) => (
            <label key={g.id} className="flex items-start gap-2 text-sm" data-testid={`migration-group-${g.id}`}>
              <input type="checkbox" checked={!!picked[g.id]} onChange={(e) => setPicked({ ...picked, [g.id]: e.target.checked })} className="mt-0.5" data-testid={`migration-group-check-${g.id}`} />
              <span>{g.label} <span className="text-xs" style={{ color: 'var(--olive)' }}>({Object.values(g.counts).reduce((a, b) => a + b, 0)} records)</span></span>
            </label>
          ))}
        </div>
        <button onClick={doExport} disabled={!!busy} data-testid="migration-export" className="inline-flex items-center gap-1 px-4 py-2 rounded text-sm font-title disabled:opacity-50" style={{ background: 'var(--maroon)', color: 'var(--cream)' }}>
          {busy === 'export' ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Export selected
        </button>
        {picked.gallery && <p className="text-xs mt-2 inline-flex items-center gap-1" style={{ color: 'var(--olive)' }}><AlertTriangle size={12} /> Gallery includes image data, so the file can be large. Export it on its own if the import fails.</p>}
      </div>

      <div className="bg-white rounded-lg p-5 border shadow-sm" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
        <h3 className="font-title text-lg mb-2" style={{ color: 'var(--navy)' }}>2. Import into this site</h3>
        <p className="text-sm mb-3" style={{ color: 'var(--olive)' }}>Choose a <code>site-data-….json</code> file exported from the other environment.</p>
        <label className="inline-flex items-center gap-1 px-4 py-2 rounded text-sm font-title cursor-pointer" style={{ background: 'var(--navy)', color: 'var(--cream)', opacity: busy ? 0.6 : 1 }} data-testid="migration-import">
          {busy === 'import' ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Import file…
          <input ref={fileRef} type="file" accept="application/json,.json" onChange={doImport} className="hidden" disabled={!!busy} data-testid="migration-import-file" />
        </label>
        {result && (
          <div className="mt-3 text-sm" data-testid="migration-import-result">
            <p className="font-title" style={{ color: 'var(--navy)' }}>Imported:</p>
            <ul className="list-disc ml-5">{Object.entries(result).map(([c, n]) => <li key={c}>{c}: {n} record{n === 1 ? '' : 's'}</li>)}</ul>
          </div>
        )}
      </div>
      <p className="text-xs mt-4" style={{ color: 'var(--olive)' }}>Have database access instead? <code>python backend/migrate_data.py export|import</code> does the same from the command line.</p>
    </div>
  );
}
